// javascript
'use client'
import { useEffect, useRef } from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";
import useGebruiker from "../../hooks/useGebruiker";

export const BugsnagClient = () => {
    const {ingelogdeGebruiker, namensBedrijf} = useGebruiker()
    const userRef = useRef({ user: null, company: null });

    // Keep userRef in sync with current user and company
    userRef.current = {
        user: ingelogdeGebruiker,
        company: namensBedrijf
    };

    // Initialize Bugsnag once on mount
    useEffect(() => {
        // Helper to add user and company metadata to an event
        const addUserAndCompanyToEvent = (event) => {
            const currentUser = userRef.current.user;
            const currentCompany = userRef.current.company;

            if (currentUser && (currentUser.id || currentUser.email || currentUser.naam)) {
                event.setUser(currentUser.id, currentUser.email, currentUser.naam);

                if (currentCompany && (currentCompany.id || currentCompany.debinr || currentCompany.naam)) {
                    event.addMetadata('company', {
                        'id': currentCompany.id,
                        'debinr': currentCompany.debinr,
                        'name': currentCompany.naam
                    });
                }
            }
        };

        if (!Bugsnag._client) {
            Bugsnag.start({
                apiKey: process.env.NEXT_PUBLIC_BUGSNAG_APIKEY,
                appVersion: process.env.NEXT_PUBLIC_VERSIE,
                releaseStage: process.env.NEXT_PUBLIC_APP_ENV,
                enabledReleaseStages: ['production', 'testing'],
                plugins: [new BugsnagPluginReact()],
                generateAnonymousId: true,
                onError: function (event) {
                    try {
                        // Add user and company metadata to every error event
                        addUserAndCompanyToEvent(event);

                        // Filter out certain HTTP error statuses
                        const status = event?.originalError?.response?.status;
                        if ([401, 403, 404, 419, 422].includes(Number(status))) return false;

                        // Filter out specific error messages
                        const errClass = event?.errors?.[0]?.errorClass;
                        const errMessage = event?.errors?.[0]?.errorMessage;
                        if (
                            errClass === 'InvalidError'
                            && errMessage === 'unhandledrejection handler received a non-error. See "unhandledrejection handler" tab for more detail.'
                        ) {
                            return false;
                        }
                    } catch (_e) {
                        // eslint-disable-next-line no-console
                        console.error('Error in Bugsnag onError hook', _e);
                    }
                    // allow event to proceed by default
                }
            });

            BugsnagPerformance.start({
                apiKey: process.env.NEXT_PUBLIC_BUGSNAG_APIKEY,
                appVersion: process.env.NEXT_PUBLIC_VERSIE,
                releaseStage: process.env.NEXT_PUBLIC_APP_ENV,
                enabledReleaseStages: ['production', 'testing'],
            });
        }

        // Handle uncaught promise rejections and forward to Bugsnag
        let handleUnhandledRejection;
        if (typeof window !== 'undefined') {
            handleUnhandledRejection = (event) => {
                try {
                    const error = event?.reason instanceof Error ? event.reason : new Error(String(event?.reason));
                    if (window.reportError) {
                        window.reportError(error);
                    } else {
                        Bugsnag.notify(error);
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                } catch (_e) {
                    // ensure this handler never throws
                    // eslint-disable-next-line no-console
                    console.error('unhandledrejection handler error', _e);
                }
            };
            window.addEventListener('unhandledrejection', handleUnhandledRejection);

            // Test to send an error to Bugsnag from the console
            // call `testBugsnag()` to trigger it
            // Note: user and company metadata will be added automatically via the onError callback
            window.testBugsnag = () => {
                try {
                    Bugsnag.notify(new Error('Test error from console'));
                } catch (_e) {
                    // eslint-disable-next-line no-console
                    console.error('testBugsnag failed', _e);
                }
            };
        }

        return () => {
            if (typeof window !== 'undefined' && handleUnhandledRejection) {
                window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            }
        };
    }, []); // Only run once on mount

    // Start a new Bugsnag session when user or company changes
    useEffect(() => {
        if (Bugsnag && typeof Bugsnag.startSession === 'function') {
            try {
                Bugsnag.startSession();
            } catch (_e) {
                // eslint-disable-next-line no-console
                console.error('Error starting Bugsnag session', _e);
            }
        }
    }, [ingelogdeGebruiker, namensBedrijf]);


    return <></>
}
